/**
 * Centralized Date Service for Jobvious
 *
 * STRATEGY:
 * - Work dates (availableDate, dueDate, workDate) are "calendar dates" - no timezone conversion
 * - Timestamps (createdDate, stateDate, quoteSentAt) are UTC - always convert for display
 * - Google Calendar events need installer's timezone
 *
 * DATABASE: All dates stored in UTC
 * DISPLAY: Converted to user's local timezone (for timestamps) or shown as-is (for dates)
 */

import moment from 'moment-timezone';
import jstz from 'jstz';

class DateService {
  constructor() {
    // Auto-detect user's timezone from browser
    this._userTimezone = null;
  }

  /**
   * Get user's timezone (cached after first call)
   */
  getUserTimezone() {
    if (!this._userTimezone) {
      this._userTimezone = jstz.determine().name();
    }
    return this._userTimezone;
  }

  /**
   * Get timezone abbreviation (e.g., "EST", "PST")
   */
  getTimezoneAbbr() {
    return moment.tz(this.getUserTimezone()).format('z');
  }

  /**
   * Get timezone offset (e.g., "-05:00")
   */
  getTimezoneOffset() {
    return moment.tz(this.getUserTimezone()).format('Z');
  }

  // ============================================================
  // DATE-ONLY FIELDS (availableDate, dueDate, workDate)
  // These are calendar dates with no time component
  // ============================================================

  /**
   * Format a date for sending to API (date-only fields)
   * @param {Date|moment|string} date - The date to format
   * @returns {string} YYYY-MM-DD format
   */
  toAPIDate(date) {
    if (!date) return null;
    return moment(date).format('YYYY-MM-DD');
  }

  /**
   * Parse a date-only field from API for display
   * NO timezone conversion - treat as local calendar date
   * @param {string} dateString - Date string from API
   * @returns {moment} Moment object (local)
   */
  fromAPIDate(dateString) {
    if (!dateString) return null;
    // Extract just YYYY-MM-DD portion and parse as local
    const dateOnly = String(dateString).substring(0, 10);
    return moment(dateOnly, 'YYYY-MM-DD');
  }

  /**
   * Display a date-only field
   * @param {string} dateString - Date from API
   * @param {string} format - Display format (default: 'MMM D, YYYY')
   * @returns {string} Formatted date string
   */
  displayDate(dateString, format = 'MMM D, YYYY') {
    const m = this.fromAPIDate(dateString);
    return m ? m.format(format) : '';
  }

  // ============================================================
  // TIMESTAMP FIELDS (createdDate, stateDate, quoteSentAt)
  // These are exact moments in time, stored in UTC
  // ============================================================

  /**
   * Format a timestamp for sending to API (always UTC)
   * @param {Date|moment|string} date - The date/time to format
   * @returns {string} ISO 8601 format in UTC
   */
  toAPITimestamp(date) {
    if (!date) return null;
    return moment(date).utc().format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * Parse a UTC timestamp from API and convert to user's local timezone
   * @param {string} utcString - UTC timestamp from API
   * @returns {moment} Moment object in user's timezone
   */
  fromAPITimestamp(utcString) {
    if (!utcString) return null;
    return moment.utc(utcString).tz(this.getUserTimezone());
  }

  /**
   * Display a timestamp field (converted to local timezone)
   * @param {string} utcString - UTC timestamp from API
   * @param {string} format - Display format (default: 'MMM D, YYYY h:mm A')
   * @returns {string} Formatted date/time string in local timezone
   */
  displayTimestamp(utcString, format = 'MMM D, YYYY h:mm A') {
    const m = this.fromAPITimestamp(utcString);
    return m ? m.format(format) : '';
  }

  /**
   * Display a timestamp with timezone indicator
   * @param {string} utcString - UTC timestamp from API
   * @returns {string} e.g., "Dec 8, 2025 2:30 PM (EST)"
   */
  displayTimestampWithTZ(utcString) {
    const m = this.fromAPITimestamp(utcString);
    if (!m) return '';
    return `${m.format('MMM D, YYYY h:mm A')} (${m.format('z')})`;
  }

  // ============================================================
  // SCHEDULED WORK DATES
  // Combine a date with operating hours to create meaningful timestamps
  // ============================================================

  /**
   * Build a scheduled start/end datetime from a date and location operating hours time
   * @param {string|Date|moment} date - Selected date
   * @param {string} time - Time string from operating hours (e.g., "08:00" or "17:00")
   * @returns {string} UTC timestamp "YYYY-MM-DD HH:mm:ss"
   */
  toScheduledDateTime(date, time) {
    if (!date) return null;
    const dateStr = moment(date).format('YYYY-MM-DD');
    const localDateTime = moment.tz(`${dateStr} ${time}`, 'YYYY-MM-DD HH:mm', this.getUserTimezone());
    return localDateTime.utc().format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * Get open/close time for a specific date from an operating hours array
   * Falls back to 08:00/17:00 if the day is closed or hours not found
   * @param {Array} hours - Operating hours array [{day_of_week, open_time, close_time, is_open}]
   * @param {string|Date|moment} date - The date to look up
   * @param {string} which - 'open' or 'close'
   * @returns {string} Time string e.g. "08:00"
   */
  getTimeForDate(hours, date, which = 'open') {
    const dayOfWeek = moment(date).day();
    const dayHours = hours?.find(h => h.day_of_week === dayOfWeek);
    if (which === 'close') {
      return dayHours?.close_time || '17:00';
    }
    return dayHours?.open_time || '08:00';
  }

  // ============================================================
  // GOOGLE CALENDAR / APPOINTMENTS
  // Need installer's timezone for correct event creation
  // ============================================================

  /**
   * Format date/time for Google Calendar event
   * @param {string} date - Date (YYYY-MM-DD)
   * @param {string} time - Time (HH:mm) - defaults to business start (09:00)
   * @param {string} timezone - IANA timezone (e.g., 'America/New_York')
   * @returns {string} ISO 8601 with timezone
   */
  toGoogleCalendarDateTime(date, time = '09:00', timezone) {
    const tz = timezone || this.getUserTimezone();
    const dateTime = moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', tz);
    return dateTime.format();
  }

  /**
   * Create Google Calendar event time object
   * @param {string} date - Date (YYYY-MM-DD)
   * @param {number} durationDays - Duration in days
   * @param {string} timezone - Installer's timezone
   * @returns {Object} { start, end } for Google Calendar API
   */
  toGoogleCalendarEvent(date, durationDays = 1, timezone) {
    const tz = timezone || this.getUserTimezone();

    // All-day event for multi-day jobs
    if (durationDays >= 1) {
      const startDate = this.toAPIDate(date);
      const endDate = moment(date).add(durationDays, 'days').format('YYYY-MM-DD');
      return {
        start: { date: startDate },
        end: { date: endDate },
        timezone: tz
      };
    }

    // Timed event for partial day jobs (default 9am-5pm)
    return {
      start: {
        dateTime: this.toGoogleCalendarDateTime(date, '09:00', tz),
        timeZone: tz
      },
      end: {
        dateTime: this.toGoogleCalendarDateTime(date, '17:00', tz),
        timeZone: tz
      }
    };
  }

  // ============================================================
  // EMAIL FORMATTING
  // Always include timezone for clarity
  // ============================================================

  /**
   * Format a date for email communication
   * @param {string} dateString - Date from API
   * @param {string} timezone - Recipient's timezone (optional)
   * @returns {string} e.g., "Monday, December 8, 2025"
   */
  forEmailDate(dateString, timezone) {
    const m = this.fromAPIDate(dateString);
    if (!m) return '';
    return m.format('dddd, MMMM D, YYYY');
  }

  /**
   * Format a timestamp for email communication (includes timezone)
   * @param {string} utcString - UTC timestamp from API
   * @param {string} timezone - Recipient's timezone (defaults to user's)
   * @returns {string} e.g., "Monday, December 8, 2025 at 2:30 PM (EST)"
   */
  forEmailTimestamp(utcString, timezone) {
    const tz = timezone || this.getUserTimezone();
    const m = moment.utc(utcString).tz(tz);
    if (!m) return '';
    return `${m.format('dddd, MMMM D, YYYY')} at ${m.format('h:mm A')} (${m.format('z')})`;
  }

  // ============================================================
  // UTILITY METHODS
  // ============================================================

  /**
   * Get current date as YYYY-MM-DD (for date-only fields)
   */
  today() {
    return moment().format('YYYY-MM-DD');
  }

  /**
   * Get current UTC timestamp (for timestamp fields)
   */
  nowUTC() {
    return moment.utc().format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * Get current moment in user's timezone
   */
  now() {
    return moment().tz(this.getUserTimezone());
  }

  /**
   * Check if a date is valid
   */
  isValid(date) {
    return date && moment(date).isValid();
  }

  /**
   * Check if date is today
   */
  isToday(dateString) {
    const date = this.fromAPIDate(dateString);
    return date && date.isSame(moment(), 'day');
  }

  /**
   * Check if date is in the past
   */
  isPast(dateString) {
    const date = this.fromAPIDate(dateString);
    return date && date.isBefore(moment(), 'day');
  }

  /**
   * Check if date is in the future
   */
  isFuture(dateString) {
    const date = this.fromAPIDate(dateString);
    return date && date.isAfter(moment(), 'day');
  }

  /**
   * Add days to a date
   * @param {string} dateString - Starting date
   * @param {number} days - Number of days to add
   * @returns {string} YYYY-MM-DD
   */
  addDays(dateString, days) {
    return moment(dateString).add(days, 'days').format('YYYY-MM-DD');
  }

  /**
   * Get difference between two dates in days
   */
  diffDays(date1, date2) {
    return moment(date1).diff(moment(date2), 'days');
  }

  // ============================================================
  // DISPLAY FORMAT PRESETS
  // Use these for consistent formatting across the app
  // ============================================================

  formats = {
    // Date-only formats
    date: {
      short: 'MMM D',           // Dec 8
      medium: 'MMM D, YYYY',    // Dec 8, 2025
      long: 'MMMM D, YYYY',     // December 8, 2025
      withDay: 'ddd, MMM D',    // Mon, Dec 8
      withDayLong: 'dddd, MMMM D, YYYY', // Monday, December 8, 2025
      iso: 'YYYY-MM-DD',        // 2025-12-08
    },
    // Timestamp formats
    timestamp: {
      short: 'MMM D, h:mm A',           // Dec 8, 2:30 PM
      medium: 'MMM D, YYYY h:mm A',     // Dec 8, 2025 2:30 PM
      long: 'MMMM D, YYYY h:mm A',      // December 8, 2025 2:30 PM
      withDay: 'ddd, MMM D h:mm A',     // Mon, Dec 8 2:30 PM
      timeOnly: 'h:mm A',               // 2:30 PM
    }
  };
}

// Export singleton instance
const dateService = new DateService();
export default dateService;

// Also export class for testing
export { DateService };
